'use client'

// Publishes `--pc-keyboard-inset`: how many pixels of the layout viewport the
// on-screen keyboard currently covers. 0 when it's closed, or when the browser
// is too old to tell us.
//
// Bottom-anchored UI (the /generate prompt bar, the chat composer) subtracts it
// so the input a user just tapped can't end up behind the keyboard — the cause
// of ~1,000 rage-clicks a day on the prompt textarea.
//
// WHY A MEASUREMENT, NOT A DEVICE CHECK
//
//   documentElement.clientHeight  = the LAYOUT viewport
//   visualViewport.height + offsetTop = the bottom edge of what's actually visible
//
// The gap between them is the keyboard. That formula is self-correcting across
// every platform behaviour, which is what makes it safe to ship everywhere:
//
//   * Browsers that shrink the layout viewport for the keyboard (older Android
//     Chrome, `interactive-widget=resizes-content`) move both numbers together,
//     so the inset stays 0 — correct, because a bottom-anchored element is
//     already sitting above the keyboard and must not be moved twice.
//   * Browsers that don't (iOS Safari, and Chrome's current `resizes-visual`
//     default) leave the layout viewport alone, so the gap IS the keyboard.
//
// It's also immune to the iOS address bar: collapsing it changes the layout and
// visual viewports by the same amount, so the difference doesn't move.
const CSS_VAR = '--pc-keyboard-inset'

// Pixels. Below this, treat it as noise rather than a keyboard — browsers report
// fractional viewport heights that wobble by a fraction of a pixel between frames.
const NOISE_FLOOR_PX = 1

function measureInset() {
  const vv = window.visualViewport
  if (!vv) return 0

  // Pinch-zoom shrinks the visual viewport too. That isn't a keyboard, and
  // shifting the layout under someone who is zoomed in fights them.
  if (vv.scale > 1.01) return 0

  const layoutBottom = document.documentElement.clientHeight
  const visibleBottom = vv.height + vv.offsetTop
  const inset = layoutBottom - visibleBottom

  return inset > NOISE_FLOOR_PX ? Math.round(inset) : 0
}

let subscribers = 0
let frame = 0
let published = null

function flush() {
  frame = 0
  const inset = measureInset()
  // Skip the write when nothing changed: visualViewport fires a burst of events
  // during the keyboard animation and each style write costs a recalc.
  if (inset === published) return
  published = inset
  document.documentElement.style.setProperty(CSS_VAR, `${inset}px`)
}

function schedule() {
  if (frame) return
  frame = requestAnimationFrame(flush)
}

/**
 * Begin publishing the CSS variable. Ref-counted and idempotent, so several
 * components may call it; the listeners attach once and detach when the last
 * one unsubscribes. Returns the unsubscribe function.
 */
export function startKeyboardInsetTracking() {
  if (typeof window === 'undefined') return () => {}

  subscribers += 1

  if (subscribers === 1) {
    const vv = window.visualViewport
    if (vv) {
      // `resize` covers the keyboard opening and closing; `scroll` covers the
      // visual viewport being panned while it's open (offsetTop changes).
      vv.addEventListener('resize', schedule)
      vv.addEventListener('scroll', schedule)
    }
    // Some iOS versions settle the viewport a beat after focus moves, and won't
    // fire another resize on their own. Re-measuring on focus change catches it.
    window.addEventListener('focusin', schedule)
    window.addEventListener('focusout', schedule)
    window.addEventListener('orientationchange', schedule)
    flush()
  }

  return () => {
    subscribers -= 1
    if (subscribers > 0) return

    const vv = window.visualViewport
    if (vv) {
      vv.removeEventListener('resize', schedule)
      vv.removeEventListener('scroll', schedule)
    }
    window.removeEventListener('focusin', schedule)
    window.removeEventListener('focusout', schedule)
    window.removeEventListener('orientationchange', schedule)

    if (frame) {
      cancelAnimationFrame(frame)
      frame = 0
    }
    // Leave the page as we found it, so a stale inset can't strand layout.
    published = null
    document.documentElement.style.removeProperty(CSS_VAR)
  }
}
